import { PagingParameters } from "@/api/dtos";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import {
  useCountRelatedFields,
  useCreateRelatedFields,
  useDeleteRelation,
  useGetNotRelatedResource,
  useGetRelatedFields,
} from "@/api/related-fields";
import { documentsColumnsVisibility } from "@/config/dashboard/documents";
import { generateTable } from "@/config/dashboard/general";
import { documentsColumns } from "@/config/dashboard/documents";
import { Document } from "@/lib/types";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { RelatedRecordRowActions } from "@/components/table/RelatedRecordRowActions";
import { createActionsColumnDef } from "@/components/table/table-actions-column";
import ShowElements from "../drawer/ShowElements";
import { LinkableType } from "../LinksManagement";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { RelatedLinksListGridLayout } from "./RelatedLinksListGridLayout";
import { GridCardWithActions } from "./GridCardWithActions";
import { DocumentLinkGridCard } from "./relatedLinkGridCards";

interface LinksProps {
  id: string;
  type: LinkableType;
}

/**
 * ArtworkLinks component
 * @returns {JSX.Element}
 */
const DocumentLinks = ({ id, type }: LinksProps) => {
  const { t } = useTranslation();

  const [getPaginationRelatedDocuments, setPaginationRelatedDocuments] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  const { data: relatedFields } = useGetRelatedFields(
    id,
    type,
    "relatedDocument",
    getPaginationRelatedDocuments.pageSize,
    getPaginationRelatedDocuments.page * getPaginationRelatedDocuments.pageSize,
  );

  const { data: countRelatedDocuments } = useCountRelatedFields(id, type, [
    "relatedDocument",
  ]);

  const { mutate: createRelatedFields } = useCreateRelatedFields(
    id,
    type,
    "relatedDocument",
  );
  const { mutate: deleteRelation } = useDeleteRelation(
    id,
    type,
    "relatedDocument",
  );

  const [searchContent, setSearchContent] = useState("");

  const handleAddMode = () => {
    // advancedSearch.resetQuery();
    setIsAddMode(!isAddMode);
  };

  const [isAddMode, setIsAddMode] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(
    null,
  );

  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: notRelatedDocuments } = useGetNotRelatedResource<Document>({
    resultType: "document",
    resourceId: id,
    pagination: getPagination,
    searchQuery: searchContent,
  });

  const handleCreateRelation = useCallback(
    (idToLink: string) => {
      createRelatedFields([id, idToLink]);
    },
    [createRelatedFields, id],
  );

  const handleDeleteRelation = useCallback(
    (id: string) => {
      deleteRelation(id);
    },
    [deleteRelation],
  );

  const baseColumns = useMemo(
    () => generateTable(documentsColumns, "document", t),
    [t],
  );

  const renderDocumentRowActions = useCallback(
    (item: Document) => (
      <RelatedRecordRowActions
          isAddMode={isAddMode}
          resourceId={item.id}
          previewType="document"
          previewElements={[
            {
              label: t("documentsDescriptionNumeroEnregistrement"),
              value: item.documentsDescriptionNumeroEnregistrement,
              type: "text",
            },
            {
              label: t("documentsDescriptionLegendeRedigee"),
              value: item.documentsDescriptionLegendeRedigee,
              type: "richtext",
            },
            {
              label: t("attachments"),
              value: item.attachments,
              type: "image",
            },
          ]}
          onDelete={() => handleDeleteRelation(item.id)}
          onAdd={() => handleCreateRelation(item.id)}
        />
    ),
    [handleCreateRelation, handleDeleteRelation, isAddMode, t],
  );

  const columns: ColumnDef<Document, any>[] = useMemo(() => {
    return [
      ...baseColumns,
      createActionsColumnDef<Document>(({ row }) =>
        renderDocumentRowActions(row.original),
      ),
    ];
  }, [baseColumns, renderDocumentRowActions]);

  const STORAGE_KEY = "document-links-table";
  const defaultVisibility = { ...documentsColumnsVisibility, actions: true };
  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY);
      if (saved) {
        const { order } = saved;
        return order || [];
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      try {
        const saved = readTablePreferences(STORAGE_KEY);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    },
  );

  const tableOptions = {
    enableColumnResizing: true,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnsOrder,
    state: { columnVisibility, columnOrder: columnsOrder },
  };

  const relatedPaginationProps = {
    pagination: getPaginationRelatedDocuments,
    setPage: setPaginationRelatedDocuments,
    totalSize: countRelatedDocuments?.relatedDocument ?? 0,
  };

  return (
    <RelatedLinksListGridLayout
      storageKey={STORAGE_KEY}
      title={t("linkedDocuments")}
      isAddMode={isAddMode}
      onBack={handleAddMode}
      onToggleAdd={handleAddMode}
      searchInput={
        <div className="flex items-center gap-2">
          <Input
            placeholder={t("search")}
            className="w-80"
            value={searchContent}
            onChange={(e) => setSearchContent(e.target.value)}
          />
        </div>
      }
      addModeTable={
        <DataTable
          columns={columns}
          data={notRelatedDocuments?.data ?? []}
          pagination={{
            pagination: getPagination,
            setPage: setPagination,
            totalSize: notRelatedDocuments?.totalSize ?? 0,
          }}
          storageKey={STORAGE_KEY}
          options={tableOptions}
        />
      }
      listTable={
        <DataTable
          columns={columns}
          data={relatedFields ?? []}
          pagination={relatedPaginationProps}
          storageKey={STORAGE_KEY}
          options={tableOptions}
        />
      }
      gridContent={
        <>
          {relatedFields?.map((row: Document) => (
            <div key={row.id}>
              <GridCardWithActions actions={renderDocumentRowActions(row)}>
                <DocumentLinkGridCard
                item={row}
                t={t}
                onTextClick={() => setSelectedDocumentId(row.id)}
              />
              </GridCardWithActions>
              <ShowElements
                type="document"
                id={row.id}
                open={selectedDocumentId === row.id}
                onOpenChange={(open) => {
                  if (!open) {
                    setSelectedDocumentId(null);
                  }
                }}
                trigger={<span />}
                elements={[
                  {
                    label: t("documentsDescriptionNumeroEnregistrement"),
                    value: row.documentsDescriptionNumeroEnregistrement,
                    type: "text",
                  },
                  {
                    label: t("documentsDescriptionLegendeRedigee"),
                    value: row.documentsDescriptionLegendeRedigee ?? "",
                    type: "richtext",
                  },
                  {
                    label: t("attachments"),
                    value: row.attachments,
                    type: "image",
                  },
                ]}
              />
            </div>
          ))}
        </>
      }
      relatedPagination={relatedPaginationProps}
    />
  );
};

export default DocumentLinks;
